import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import {
  BarChart,
  Bar,
  Area,
  ComposedChart,
  Brush,
  Cell,
  CartesianGrid,
  ReferenceLine,
  ReferenceDot,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  ErrorBar,
  LabelList,
  Rectangle,
  ResponsiveContainer,
} from 'recharts';
import { Typography } from 'rmwc/Typography';
import { Elevation } from 'rmwc/Elevation';
import { Text } from '../Text';

export const currencyFormatter = amount => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount / 100);
};

export const dateFormatter = date => {
  if (Object.prototype.toString.call(date) !== '[object Date]') date = new Date(date);
  return new Intl.DateTimeFormat('en-US').format(date);
};

const styles = {
  legend: {
    padding: 0,
    marginTop: '8px',
    marginBottom: '-8px',
    textAlign: 'center',
  },
  legendItem: {
    display: 'inline-block',
    marginRight: '16px',
  },
  surface: {
    display: 'inline-block',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
  details: {
    boxShadow: 'none',
    border: '1px solid #d7d7d7',
    padding: '4px',
  },
  tooltip: {
    background: '#ffffff',
    marginLeft: '16px',
    padding: '8px',
    borderRadius: '2px',
  },
};

export class AreaChartComponent extends Component {
  constructor(props) {
    super(props);

    let initialMax;
    let thecolor;
    initialMax = this.props.data
      .map((entry, index) => entry['before'] + entry['change'])
      .reduce((acc, curr, Index) => Math.max(acc, curr));

    let initialBeforeMax = this.props.data
      .map((entry, index) => entry['before'])
      .reduce((acc, curr, Index) => Math.max(acc, curr));

    initialMax = Math.max(initialMax, initialBeforeMax);
    thecolor = this.props.data.map((entry, index) => (entry['change'] >= 0 ? '#09a511' : 'grey'));

    let tickInterval = this.getTickInterval(initialMax);
    let height = (parseInt(initialMax / tickInterval) + 1) * tickInterval;

    this.state = {
      highlight: -1,
      length: height,
      currHeight: initialMax,
      prevHeight: initialMax,
      tickInterval: tickInterval,
      color: thecolor,
    };

    this.onClick = this.onClick.bind(this);
    this.animationOnYaxis = this.animationOnYaxis.bind(this);
    this.animationOnBarColors = this.animationOnBarColors.bind(this);
  }

  onClick(data, index) {
    const colors = this.state.color.slice();
    if (this.state.highlight == index) {
      colors[index] = data['change'] >= 0 ? '#09a511' : 'grey';
      this.setState({ highlight: -1, color: colors });
    } else {
      if (this.state.highlight !== -1) {
        colors[this.state.highlight] = this.props.data[this.state.highlight]['change'] >= 0 ? '#09a511' : 'grey';
      }
      colors[index] = data['change'] >= 0 ? '#01820c' : 'grey';
      this.setState({ highlight: index, color: colors });
    }
  }

  getTickInterval(max) {
    const options = [
      1000,
      2000,
      5000,
      10000,
      20000,
      50000,
      100000,
      200000,
      500000,
      1000000,
      2000000,
      5000000,
      10000000,
      20000000,
      50000000,
      100000000,
      200000000,
      500000000,
      1000000000,
      2000000000,
      5000000000,
    ];
    for (let option of options) {
      if (option * 5 > max * 1.1) {
        return option;
      }
    }
  }

  animationOnYaxis(currMax, changing, speed, currLength) {
    if (speed < currLength / 125) {
      speed = currLength / 125;
    }
    if (currMax >= changing) {
      let interval = setInterval(() => {
        if (changing >= currLength) {
          clearInterval(interval);
        } else {
          this.setState({ length: changing });
          changing = currLength - changing < speed ? currLength : changing + speed;
        }
      }, 10);
    } else {
      let interval = setInterval(() => {
        if (changing <= currLength) {
          clearInterval(interval);
        } else {
          this.setState({ length: changing });
          changing = changing - currLength < speed ? currLength : changing - speed;
        }
      }, 10);
    }
  }

  animationOnBarColors(currSum, prevSum, currLength, prevLength, nextProps) {
    let newTime = Math.abs(prevSum / prevLength / (currSum / currLength - prevSum / prevLength)) * 750;
    if (newTime > 750) newTime = 500;

    const newcolor = nextProps.data.map((entry, index) => (entry['change'] >= 0 ? '#09a511' : 'grey'));
    setTimeout(() => this.setState({ color: newcolor }), newTime);
  }

  componentWillReceiveProps(nextProps) {
    const prevHeight = this.state.currHeight;
    const prevLength = this.state.length;
    let changing = prevLength;

    const currSummary = nextProps.data.map((entry, index) => entry['change'] + entry['before']);
    const prevSummary = this.props.data.map((entry, index) => entry['change'] + entry['before']);

    const currAfter = nextProps.data.map((entry, index) => entry['change']);
    const prevAfter = this.props.data.map((entry, index) => entry['change']);

    const currMax = Math.max(
      currSummary.reduce((acc, curr, currentIndex) => Math.max(acc, curr)),
      nextProps.data.map((entry, index) => entry['before']).reduce((acc, curr, currentIndex) => Math.max(acc, curr))
    );
    const prevMax = Math.max(
      prevSummary.reduce((acc, curr, currentIndex) => Math.max(acc, curr)),
      this.props.data.map((entry, index) => entry['before']).reduce((acc, curr, currentIndex) => Math.max(acc, curr))
    );

    if (currMax < prevLength && currMax > prevLength * 0.65) {
      this.animationOnBarColors(currSum, prevSum, currLength, prevLength, nextProps);
      return;
    }

    const currSum = currAfter.reduce((acc, curr, currentIndex) => acc + curr);
    const prevSum = prevAfter.reduce((acc, curr, currentIndex) => acc + curr);
    this.setState({ prevHeight: prevMax, currHeight: currMax });

    //Anmiation on the yAxis
    const newInterval = this.getTickInterval(currMax);
    this.setState({ tickInterval: newInterval });

    const currLength = currMax + newInterval - currMax % newInterval;

    const speed = Math.abs(Math.round((currLength - prevLength) / 20));
    this.animationOnYaxis(currMax, changing, speed, currLength);

    //Animation on the color change
    this.animationOnBarColors(currSum, prevSum, currLength, prevLength, nextProps);
  }

  render() {
    const { classes, data, width } = this.props;
    const { prevHeight, currHeight, tickInterval, length, highlight, color } = this.state;

    const currentIndex = highlight;

    if (typeof data !== 'object')
      return (
        <div>
          <button>Loading</button>
        </div>
      );

    // const customTicks = () => {
    //   let interval =
    //     length > prevHeight * 1.25 || length < prevHeight * 0.85 ? tickInterval : (prevHeight - prevHeight % 5000) / 5;
    //   let array = [];
    //   for (let i = 0; i < 10; i++) array.push(interval * i);
    //   return array;
    // };
    const customTicks2 = () => {
      let interval =
        length > prevHeight * 1.1 || length < prevHeight * 0.9 ? this.getTickInterval(prevHeight) : tickInterval;
      let array = [];
      for (let i = 0; i < 11; i++) {
        array.push(interval * i);
        if (interval * i > this.state.currHeight) {
          break;
        }
      }
      return array;
    };

    const description = data[currentIndex];

    const primaryColor = '#356f93';
    const secondaryColor = this.props.afterColor || '#10cf9b';

    return (
      <div>
        <ul style={styles.legend} className="recharts-default-legend">
          <li style={styles.legendItem} className="recharts-legend-item legend-item-0">
            <svg
              style={styles.surface}
              className="recharts-surface"
              width="14"
              height="14"
              viewBox="0 0 32 32"
              version="1.1"
            >
              <path stroke="none" fill={primaryColor} d="M0,0h32v27h-32z" className="recharts-legend-icon" />
            </svg>
            <span className="recharts-legend-item-text">{this.props.beforeLegend || 'Current Plan'}</span>
          </li>
          {this.props.proposed && (
            <li style={styles.legendItem} className="recharts-legend-item legend-item-1">
              <svg
                style={styles.surface}
                className="recharts-surfaces"
                width="14"
                height="14"
                viewBox="0 0 32 32"
                version="1.1"
              >
                <path stroke="none" fill={secondaryColor} d="M0,0h32v27h-32z" className="recharts-legend-icon" />
              </svg>
              <span className="recharts-legend-item-text">{this.props.afterLegend || 'Proposed Change'}</span>
            </li>
          )}
        </ul>
        <ResponsiveContainer width="100%" aspect={4.0 / 1.5}>
          <ComposedChart data={data} barSize={40} margin={{ right: 25, left: 25, top: 25, bottom: 25 }}>
            <XAxis height={this.props.xAxisHeight} tick={this.props.xTick} interval={0} ticks={this.props.xTicks} />
            <YAxis
              domain={[0, this.state.length]}
              allowDataOverflow={true}
              interval={0}
              tickFormatter={currencyFormatter}
              ticks={customTicks2()}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip
              content={
                <CustomToolTips
                  beforeLegend={this.props.beforeLegend}
                  afterLegend={this.props.afterLegend}
                  proposed={this.props.proposed}
                />
              }
            />
            <Area
              name={this.props.beforeLegend || 'Current Plan'}
              dataKey="before"
              stackId="a"
              onClick={this.onClick}
              animationBegin={0}
              animationDuration={750}
              fill={primaryColor}
              strokeOpacity={0}
              fillOpacity="1"
            />
            {this.props.useAfterLine ? (
              <Line
                type="monotone"
                name={this.props.afterLegend || 'Proposed Change'}
                dataKey="after"
                stackId="a"
                onClick={this.onClick}
                animationBegin={0}
                animationDuration={750}
                fill={secondaryColor}
                stroke={secondaryColor}
                dot={false}
              />
            ) : (
              <Area
                name={this.props.afterLegend || 'Proposed Change'}
                dataKey="change"
                stackId="a"
                onClick={this.onClick}
                animationBegin={0}
                animationDuration={750}
                fill={secondaryColor}
                strokeOpacity={0}
                fillOpacity="0.6"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        {description && (
          <div>
            <div>
              Date: {description.date}
              <br />
              Before: ${description.before}
            </div>
            <div>
              Date: {description.date}
              <br />
              After: ${description.after}
            </div>
            <div>
              Date: {description.date}
              <br />
              Change: ${description.change}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export class CustomToolTips extends Component {
  render() {
    const { active, classes } = this.props;

    if (active) {
      const { payload, label } = this.props;

      const before = Math.round(payload[0].payload.before);
      const after = Math.round(payload[0].payload.after);

      return (
        <Elevation z={2} theme="background" className="p-2">
          <Typography tag="div" use="subheader2">
            <Typography use="body2">Year:</Typography> {new Date().getFullYear() + label}
          </Typography>
          <Typography tag="div" use="body1">
            <Typography use="body2">{this.props.beforeLegend || 'Current Plan'}:</Typography>{' '}
            {currencyFormatter(before)}
          </Typography>
          {this.props.proposed && (
            <Typography tag="div" use="body1">
              <Typography use="body2">{this.props.afterLegend || 'Proposed Change'}:</Typography>{' '}
              {currencyFormatter(after)}
            </Typography>
          )}
        </Elevation>
      );
    }

    return null;
  }
}

export default AreaChartComponent;
